import moment from 'moment';
import 'moment/locale/es';
import { getEventColor } from './calendarEventTypes';

// Toma todos los datos y los convierte en eventos para mostrar en el calendario
export function processEvents({ contracts, generatedDebts, generatedCredits, assetSales, bills, googleEvents, dashboardModules = ['assets', 'contracts', 'debts', 'credits', 'accounts', 'facturacion', 'income', 'calendar'] }) {
	const events = [];

	// Agregar eventos de contratos (si Contracts o Income module está habilitado)
	if ((dashboardModules.includes('contracts') || dashboardModules.includes('income')) && contracts && Array.isArray(contracts)) {
		contracts.forEach(contract => {
			const ownerName = contract.owner?.name || contract.owner?.toString() || 'Sin propietario';
			const propertyName = contract.property?.name || contract.property?.toString() || 'Sin propiedad';
			const contractLabel = contract.customId || `${ownerName} - ${propertyName}`;
			
			// Cuando vence el contrato
			if (contract.periodEnd) {
				events.push({
					id: `contract-expiration-${contract._id}`,
					type: 'contract-expiration',
					date: moment.utc(contract.periodEnd).startOf('day').toDate(),
					title: `Vencimiento de Contrato de Alquiler: ${contractLabel}`,
					description: `Vencimiento del contrato de alquiler - ${ownerName}`,
					link: `/AdminScreen/contracts/contract/${contract._id}`,
					entityType: 'contract',
					entityId: contract._id,
					color: getEventColor('contract-expiration'),
					metadata: {
						contract: contract,
						amount: contract.amount,
						currency: contract.currency,
						ownerName,
						propertyName,
					},
				});
			}

			// Cuando se cierra el contrato
			if (contract.closeDate) {
				events.push({
					id: `contract-close-${contract._id}`,
					type: 'contract-close',
					date: moment.utc(contract.closeDate).startOf('day').toDate(),
					title: `Finalización Contrato: ${contractLabel}`,
					description: `Finalización del contrato - ${ownerName}`,
					link: `/AdminScreen/contracts/contract/${contract._id}`,
					entityType: 'contract',
					entityId: contract._id,
					color: getEventColor('contract-close'),
					metadata: {
						contract: contract,
						ownerName,
						propertyName,
					},
				});
			}

			// Ingresos de inquilinos (fecha de inicio de contrato)
			// Si están habilitados 'contracts' O 'income'
			if (contract.periodInit && (dashboardModules.includes('contracts') || dashboardModules.includes('income'))) {
				events.push({
					id: `tenant-income-${contract._id}`,
					type: 'tenant-income',
					date: moment.utc(contract.periodInit).startOf('day').toDate(),
					title: `Ingresos de Inquilinos: ${contractLabel}`,
					description: `Ingresos de inquilinos - ${ownerName}`,
					link: `/AdminScreen/contracts/contract/${contract._id}`,
					entityType: 'contract',
					entityId: contract._id,
					color: getEventColor('tenant-income'),
					metadata: {
						contract: contract,
						amount: contract.amount,
						currency: contract.currency,
						ownerName,
						propertyName,
					},
				});
			}
		});
	}

	// Juntar todas las cuotas en un solo array
	const allGeneratedDebts = [];
	if (generatedDebts) {
		if (Array.isArray(generatedDebts)) {
			allGeneratedDebts.push(...generatedDebts);
		} else {
			if (generatedDebts.pending) {
				allGeneratedDebts.push(...generatedDebts.pending);
			}
			if (generatedDebts.overdue) {
				allGeneratedDebts.push(...generatedDebts.overdue);
			}
		}
	}

	// Hoy para saber qué está vencido
	const today = moment.utc().startOf('day');

	allGeneratedDebts.forEach(generatedDebt => {
		// Solo las que no están pagadas
		if (generatedDebt.paid === true) return;
		if (!generatedDebt.expiration) return;

		// Ver si es de un contrato (alquiler) o de otra deuda
		// El backend ya popula debt -> owner en /generatedDebts/search
		const debt = generatedDebt.debt;
		if (!debt) return;

		const debtId = debt?._id || debt?.toString();

		// Ver si ya venció o todavía no
		const expirationDate = moment.utc(generatedDebt.expiration).startOf('day');
		const isOverdue = expirationDate.isBefore(today);

		// Si tiene contract, es de un alquiler
		const contractRef = debt && typeof debt === 'object' && debt.contract;
		const hasContract = !!contractRef;

		if (hasContract && dashboardModules.includes('contracts')) {
			// Es una cuota de alquiler, agregarla al calendario (solo si Contracts module está habilitado)
			const contractId = contractRef?._id || contractRef?.toString() || contractRef;
			const ownerName = debt?.owner?.name || 'Sin propietario';
			const dueLabel = generatedDebt.customId || `Cuota ${moment.utc(generatedDebt.expiration).format('DD/MM/YYYY')}`;
			const eventType = isOverdue ? 'rental-due-overdue' : 'rental-due-expiration';
			const titlePrefix = isOverdue ? 'Cuota Alquiler VENCIDA' : 'Cuota Alquiler';

			events.push({
				id: `${eventType}-${generatedDebt._id}`,
				type: eventType,
				date: moment.utc(generatedDebt.expiration).toDate(),
				title: `${titlePrefix}: ${dueLabel} - ${ownerName}`,
				description: isOverdue
					? `Cuota de alquiler vencida - ${ownerName}`
					: `Vencimiento de cuota de alquiler - ${ownerName}`,
				link: `/AdminScreen/contracts/contract/${contractId}`,
				entityType: 'contract',
				entityId: contractId,
				color: getEventColor(eventType),
				metadata: {
					generatedDebt: generatedDebt,
					debt: debt,
					contract: contractId,
					amount: generatedDebt.amount,
					paid: generatedDebt.paid,
					ownerName,
					isOverdue,
				},
				isRental: true,
				isOverdue,
			});
		} else if (dashboardModules.includes('debts')) {
			// Es otra cuota (solo si Debts module está habilitado)
			const ownerName = debt?.owner?.name || 'Sin propietario';
			const concept = debt?.concept || 'Deuda';
			const dueLabel = generatedDebt.customId || `Cuota ${moment.utc(generatedDebt.expiration).format('DD/MM/YYYY')}`;
			const eventType = isOverdue ? 'debt-due-overdue' : 'debt-due-expiration';
			const titlePrefix = isOverdue ? 'Otra Cuota VENCIDA' : 'Otra Cuota';

			events.push({
				id: `${eventType}-${generatedDebt._id}`,
				type: eventType,
				date: moment.utc(generatedDebt.expiration).toDate(),
				title: `${titlePrefix} ${concept}: ${dueLabel} - ${ownerName}`,
				description: isOverdue
					? `Otra cuota de ${concept} vencida - ${ownerName}`
					: `Vencimiento de otra cuota de ${concept} - ${ownerName}`,
				link: `/AdminScreen/debts/debt/${debtId}`,
				entityType: 'debt',
				entityId: debtId,
				color: getEventColor(eventType),
				metadata: {
					generatedDebt: generatedDebt,
					debt: debt,
					amount: generatedDebt.amount,
					paid: generatedDebt.paid,
					ownerName,
					concept,
					isOverdue,
				},
				isRental: false,
				isOverdue,
			});
		}
	});

	// Juntar todas las cuotas de créditos en un solo array
	const allGeneratedCredits = [];
	if (generatedCredits) {
		if (Array.isArray(generatedCredits)) {
			allGeneratedCredits.push(...generatedCredits);
		} else {
			if (generatedCredits.pending) {
				allGeneratedCredits.push(...generatedCredits.pending);
			}
			if (generatedCredits.overdue) {
				allGeneratedCredits.push(...generatedCredits.overdue);
			}
		}
	}

	// Procesar cuotas de créditos (pasividades) - solo si Credits module está habilitado
	if (dashboardModules.includes('credits')) {
		allGeneratedCredits.forEach(generatedCredit => {
		// Solo las que no están pagadas
			if (generatedCredit.paid === true) return;
			if (!generatedCredit.expiration) return;

			// Ver si ya venció o todavía no
			const expirationDate = moment.utc(generatedCredit.expiration).startOf('day');
			const isOverdue = expirationDate.isBefore(today);

			// Obtener el crédito completo
			// El backend ya popula credit -> owner en /generatedCredits/search
			const credit = generatedCredit.credit;
			if (!credit) return;

			const creditId = credit?._id || credit?.toString();

			// Todas las cuotas de créditos son "otras cuotas" de pasividades
			const ownerName = credit?.owner?.name || 'Sin propietario';
			const concept = credit?.concept || 'Pasividad';
			const dueLabel = generatedCredit.customId || `Cuota ${moment.utc(generatedCredit.expiration).format('DD/MM/YYYY')}`;
			const eventType = isOverdue ? 'credit-due-overdue' : 'credit-due-expiration';
			const titlePrefix = isOverdue ? 'Cuota Pasividad VENCIDA' : 'Cuota Pasividad';

			events.push({
				id: `${eventType}-${generatedCredit._id}`,
				type: eventType,
				date: moment.utc(generatedCredit.expiration).toDate(),
				title: `${titlePrefix} ${concept}: ${dueLabel} - ${ownerName}`,
				description: isOverdue
					? `Cuota de pasividad ${concept} vencida - ${ownerName}`
					: `Vencimiento de cuota de pasividad ${concept} - ${ownerName}`,
				link: `/AdminScreen/credits/credit/${creditId}`,
				entityType: 'credit',
				entityId: creditId,
				color: getEventColor(eventType),
				metadata: {
					generatedCredit: generatedCredit,
					credit: credit,
					amount: generatedCredit.amount,
					paid: generatedCredit.paid,
					ownerName,
					concept,
					isOverdue,
				},
				isOverdue,
			});
		});
	}

	// Agregar Ingresos de propietarios (fecha de venta, luego será fecha de entrega de llaves [TBD])
	// Si están habilitados 'assets' O 'income'
	if ((dashboardModules.includes('assets') || dashboardModules.includes('income')) && assetSales && Array.isArray(assetSales)) {
		assetSales.forEach(sale => {
			if (sale.date) {
				const purchaserName = sale.purchaser?.name || sale.purchaser?.toString() || 'Sin comprador';
				const assetName = sale.asset?.name || sale.asset?.toString() || 'Sin activo';
				const saleLabel = sale.customId || `${purchaserName} - ${assetName}`;
				// Evento de Ingresos de propietarios
				events.push({
					id: `owner-income-${sale._id}`,
					type: 'owner-income',
					date: moment.utc(sale.date).startOf('day').toDate(),
					title: `Ingresos de Propietarios: ${saleLabel}`,
					description: `Ingresos de propietarios - ${purchaserName}`,
					link: `/AdminScreen/assetSales/assetSale/${sale._id}`,
					entityType: 'assetSale',
					entityId: sale._id,
					color: getEventColor('owner-income'),
					metadata: {
						sale: sale,
						amount: sale.amount,
						currency: sale.currency,
						purchaserName,
						assetName,
					},
				});
			}
		});
	}

	// Agregar eventos de bills (gastos) - solo si Assets module está habilitado
	if (dashboardModules.includes('assets') && bills && Array.isArray(bills)) {
		const today = moment.utc().startOf('day');
		
		bills.forEach(bill => {
			if (!bill.expirationDate) return;
			
			const expirationDate = moment.utc(bill.expirationDate).startOf('day');
			const isOverdue = expirationDate.isBefore(today);
			
			if (bill.paid === true) return;
			
			const assetName = bill.asset?.name || bill.asset?.toString() || 'Sin activo';
			const ownerName = bill.asset?.owner?.name || bill.asset?.owner?.toString() || 'Sin propietario';
			const billLabel = bill.customId || bill.name || 'Gasto';
			
			const eventType = isOverdue ? 'bill-overdue' : 'bill-expiration';
			const titlePrefix = isOverdue ? 'Gasto VENCIDO' : 'Vencimiento de Gasto';
			
			events.push({
				id: `${eventType}-${bill._id}`,
				type: eventType,
				date: moment.utc(bill.expirationDate).toDate(),
				title: `${titlePrefix}: ${billLabel} - ${assetName}`,
				description: isOverdue 
					? `Gasto vencido - ${assetName} - ${ownerName}` 
					: `Vencimiento de gasto - ${assetName} - ${ownerName}`,
				link: `/AdminScreen/bills/bill/${bill._id}`,
				entityType: 'bill',
				entityId: bill._id,
				color: getEventColor(eventType),
				metadata: {
					bill: bill,
					amount: bill.amount,
					assetName,
					ownerName,
					isOverdue,
					paid: bill.paid,
				},
				isOverdue,
			});
		});
	}

	// Agregar eventos de Google Calendar (si el usuario conectó su cuenta)
	if (googleEvents && Array.isArray(googleEvents)) {
		googleEvents.forEach(googleEvent => {
			// Timed events have `dateTime` (with offset); all-day events only have `date` (YYYY-MM-DD)
			const isAllDay = !googleEvent.start?.dateTime;
			const startValue = googleEvent.start?.dateTime || googleEvent.start?.date;
			if (!startValue) return;

			events.push({
				id: `google-event-${googleEvent.id}`,
				type: 'google-event',
				date: isAllDay ? moment.utc(startValue).startOf('day').toDate() : moment(startValue).toDate(),
				title: googleEvent.summary || '(Sin título)',
				description: googleEvent.description || '',
				externalLink: googleEvent.htmlLink,
				entityType: 'googleEvent',
				entityId: googleEvent.id,
				color: getEventColor('google-event'),
				metadata: {
					start: googleEvent.start,
					end: googleEvent.end,
					location: googleEvent.location,
					htmlLink: googleEvent.htmlLink,
				},
			});
		});
	}

	return events;
}

